import React, { useState, useEffect, useRef, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { callApi } from "../api/api";
import {
  PlusCircle,
  FileEdit,
  ArrowLeft,
  Calendar,
  Layers,
  Bookmark,
  AlignLeft,
  User,
  Clock,
  Tag,
  HelpCircle,
  Search,
  Sparkles,
  CheckCircle2,
  X,
  RefreshCw,
  ExternalLink,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────────
   THEME TOKEN
│───────────────────────────────────────────────────────────── */
const THEME = "rgb(37, 99, 235)";
// const THEME_HOVER = "rgb(29, 78, 216)";
const THEME_LIGHT = "rgba(37, 99, 235, 0.08)";

/* ─────────────────────────────────────────────────────────────
   SEARCHABLE SELECT COMPONENT
│───────────────────────────────────────────────────────────── */
function SearchableSelect({
  options = [],       // [{ value, label, sub }]
  value,
  onChange,
  placeholder = "Select…",
  disabled = false,
  loading = false,
  id,
  showSearch = false, // added to selectively show the search bar
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const optionsRefs = useRef([]);

  const selected = options.find((o) => o.value === value);

  const filtered = (showSearch && search.trim())
    ? options.filter(
      (o) =>
        o.label.toLowerCase().includes(search.toLowerCase()) ||
        (o.sub && o.sub.toLowerCase().includes(search.toLowerCase()))
    )
    : options;

  /* close on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* focus handling on open */
  useEffect(() => {
    if (open) {
      const selectedIndex = filtered.findIndex((o) => o.value === value);
      setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);

      if (showSearch && searchRef.current) {
        searchRef.current.focus();
      }
    } else {
      setFocusedIndex(-1);
    }
  }, [open, showSearch, filtered.length, value]);

  /* scroll focused option into view */
  useEffect(() => {
    if (focusedIndex >= 0 && optionsRefs.current[focusedIndex]) {
      optionsRefs.current[focusedIndex].scrollIntoView({
        block: "nearest",
      });
    }
  }, [focusedIndex]);

  const handleToggle = () => {
    if (disabled || loading) return;
    setOpen((prev) => !prev);
    setSearch("");
  };

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
    setSearch("");
    if (triggerRef.current) {
      triggerRef.current.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (disabled || loading) return;

    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (filtered.length > 0) {
          setFocusedIndex((prev) => (prev + 1) % filtered.length);
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (filtered.length > 0) {
          setFocusedIndex((prev) => (prev - 1 + filtered.length) % filtered.length);
        }
        break;
      case "Enter":
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filtered.length) {
          handleSelect(filtered[focusedIndex].value);
        } else if (filtered.length > 0) {
          handleSelect(filtered[0].value);
        }
        break;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        setSearch("");
        if (triggerRef.current) {
          triggerRef.current.focus();
        }
        break;
      case "Tab":
        setOpen(false);
        setSearch("");
        break;
      default:
        break;
    }
  };

  return (
    <div ref={wrapRef} onKeyDown={handleKeyDown} style={{ position: "relative", width: "100%", zIndex: open ? 1000 : 1 }} id={id}>
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        className="ado-focusable"
        onClick={handleToggle}
        disabled={disabled || loading}
        style={{
          ...ss.trigger,
          opacity: disabled ? 0.55 : 1,
          cursor: disabled || loading ? "not-allowed" : "pointer",
          borderColor: open ? THEME : "#cbd5e1",
          boxShadow: open ? `0 0 0 3px ${THEME_LIGHT}` : "none",
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span style={ss.triggerText}>
          {loading ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", color: "#2563eb", fontWeight: "600" }}>
              <RefreshCw size={13} style={{ animation: "spinAnimation 1s linear infinite" }} />
              <span>{placeholder && placeholder.includes("Refetching") ? placeholder : "Loading…"}</span>
            </span>
          ) : selected ? (
            <span>
              {selected.label}
              {selected.sub && (
                <span style={ss.triggerSub}> · {selected.sub}</span>
              )}
            </span>
          ) : value ? (
            <span style={{ color: "#1e293b", fontWeight: "600" }}>{value}</span>
          ) : (
            <span style={{ color: "#94a3b8" }}>{placeholder}</span>
          )}
        </span>
        <svg
          style={{
            ...ss.chevron,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="5 8 10 13 15 8" />
        </svg>
      </button>

      {/* Dropdown panel */}
      {open && (
        <div style={ss.panel} role="listbox">
          {/* Search bar */}
          {showSearch && (
            <div style={ss.searchWrap}>
              <svg style={ss.searchIcon} viewBox="0 0 20 20" fill="none" stroke="#94a3b8" strokeWidth="2">
                <circle cx="8.5" cy="8.5" r="5.5" />
                <line x1="13" y1="13" x2="17" y2="17" />
              </svg>
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
                style={ss.searchInput}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  style={ss.clearBtn}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
          )}

          {/* Options list */}
          <div style={ss.optionList}>
            {filtered.length === 0 ? (
              <div style={ss.emptyMsg}>No results found</div>
            ) : (
              filtered.map((o, idx) => {
                const isActive = o.value === value;
                const isFocused = idx === focusedIndex;
                return (
                  <button
                    key={o.value}
                    ref={(el) => (optionsRefs.current[idx] = el)}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    tabIndex={-1}
                    onClick={() => handleSelect(o.value)}
                    onMouseEnter={() => setFocusedIndex(idx)}
                    style={{
                      ...ss.option,
                      background: isActive ? THEME_LIGHT : "transparent",
                      outline: isFocused ? `1px solid ${THEME}` : "none",
                      outlineOffset: "-1px",
                      color: (isActive || isFocused) ? THEME : "#0f172a",
                      fontWeight: isActive ? 600 : 400,
                    }}
                  >
                    <span style={ss.optionLabel}>{o.label}</span>
                    {o.sub && <span style={ss.optionSub}>{o.sub}</span>}
                    {isActive && (
                      <svg style={ss.checkIcon} viewBox="0 0 20 20" fill={THEME}>
                        <path d="M5 10l4 4 6-8" stroke={THEME} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   CUSTOM DATE PICKER COMPONENT
   ───────────────────────────────────────────────────────────── */
function CustomDatePicker({
  value, // "YYYY-MM-DD"
  onChange, // function(val)
  placeholder = "Select date…",
  disabled = false,
  id,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Parse current date or default to today's month/year for navigation
  const today = new Date();
  const initialDate = value ? new Date(value) : today;
  const [navDate, setNavDate] = useState(initialDate);

  // Sync navDate when value changes externally
  useEffect(() => {
    if (value) {
      setNavDate(new Date(value));
    }
  }, [value]);

  /* close on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleToggle = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
  };

  const selectDate = (date) => {
    // Format to YYYY-MM-DD
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    onChange(`${y}-${m}-${d}`);
    setOpen(false);
  };

  const handleClear = () => {
    onChange("");
    setOpen(false);
  };

  const handleToday = () => {
    selectDate(new Date());
  };

  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const canGoPrev = navDate.getFullYear() > today.getFullYear() ||
    (navDate.getFullYear() === today.getFullYear() && navDate.getMonth() > today.getMonth());

  // Nav actions
  const prevMonth = () => {
    if (!canGoPrev) return;
    setNavDate(new Date(navDate.getFullYear(), navDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setNavDate(new Date(navDate.getFullYear(), navDate.getMonth() + 1, 1));
  };

  // Generate calendar days
  const year = navDate.getFullYear();
  const month = navDate.getMonth(); // 0-indexed

  // First day of current month (0 = Sunday, 1 = Monday, etc.)
  const firstDayIndex = new Date(year, month, 1).getDay();
  // Total days in current month
  const totalDays = new Date(year, month + 1, 0).getDate();
  // Total days in previous month
  const prevTotalDays = new Date(year, month, 0).getDate();

  const daysGrid = [];

  // Previous month days to fill index gap
  for (let i = firstDayIndex - 1; i >= 0; i--) {
    daysGrid.push({
      date: new Date(year, month - 1, prevTotalDays - i),
      isCurrentMonth: false,
    });
  }

  // Current month days
  for (let i = 1; i <= totalDays; i++) {
    daysGrid.push({
      date: new Date(year, month, i),
      isCurrentMonth: true,
    });
  }

  // Next month days to fill to 42 items (6 rows)
  const remaining = 42 - daysGrid.length;
  for (let i = 1; i <= remaining; i++) {
    daysGrid.push({
      date: new Date(year, month + 1, i),
      isCurrentMonth: false,
    });
  }

  // Format selected value for display
  const formatDateDisplay = (val) => {
    if (!val) return "";
    const [y, m, d] = val.split("-");
    const dateObj = new Date(Number(y), Number(m) - 1, Number(d));
    if (isNaN(dateObj.getTime())) return val;
    return dateObj.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const selectedStr = value ? value : "";
  const displayVal = formatDateDisplay(selectedStr);

  return (
    <div
      ref={wrapRef}
      onKeyDown={(e) => {
        if (e.key === "Escape") setOpen(false);
      }}
      style={{ position: "relative", width: "100%", zIndex: open ? 1000 : 1 }}
      id={id}
    >
      {/* Trigger */}
      <button
        type="button"
        className="ado-focusable"
        onClick={handleToggle}
        disabled={disabled}
        style={{
          ...ss.trigger,
          opacity: disabled ? 0.55 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
          borderColor: open ? THEME : "#cbd5e1",
          boxShadow: open ? `0 0 0 3px ${THEME_LIGHT}` : "none",
        }}
      >
        <span style={ss.triggerText}>
          {displayVal ? (
            <span>{displayVal}</span>
          ) : (
            <span style={{ color: "#94a3b8" }}>{placeholder}</span>
          )}
        </span>
        {/* Calendar Icon */}
        <svg
          style={{ width: 18, height: 18, flexShrink: 0, color: "#64748b" }}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </button>

      {/* Popover */}
      {open && (
        <div style={{
          ...ss.panel,
          padding: "14px",
          width: "300px",
          boxSizing: "border-box",
          top: "auto",
          bottom: "calc(100% + 4px)"
        }}>
          {/* Header */}
          <div style={dp.header}>
            <button
              type="button"
              onClick={prevMonth}
              disabled={!canGoPrev}
              style={{
                ...dp.navBtn,
                opacity: canGoPrev ? 1 : 0.3,
                cursor: canGoPrev ? "pointer" : "not-allowed",
              }}
            >
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}>
                <polyline points="12 5 7 10 12 15" />
              </svg>
            </button>
            <span style={dp.navTitle}>
              {monthNames[month]} {year}
            </span>
            <button type="button" onClick={nextMonth} style={dp.navBtn}>
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}>
                <polyline points="8 5 13 10 8 15" />
              </svg>
            </button>
          </div>

          {/* Weekday headers */}
          <div style={dp.weekdaysGrid}>
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
              <span key={day} style={dp.weekday}>
                {day}
              </span>
            ))}
          </div>

          {/* Days Grid */}
          <div style={dp.daysGrid}>
            {daysGrid.map(({ date, isCurrentMonth }, idx) => {
              const dateY = date.getFullYear();
              const dateM = String(date.getMonth() + 1).padStart(2, "0");
              const dateD = String(date.getDate()).padStart(2, "0");
              const dateStr = `${dateY}-${dateM}-${dateD}`;
              const isSelected = dateStr === selectedStr;

              const isToday =
                date.getDate() === today.getDate() &&
                date.getMonth() === today.getMonth() &&
                date.getFullYear() === today.getFullYear();

              const cellDateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
              const isPast = cellDateStart < todayStart;

              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => selectDate(date)}
                  disabled={isPast}
                  style={{
                    ...dp.dayCell,
                    color: isSelected
                      ? "#ffffff"
                      : isPast
                        ? "#cbd5e1"
                        : isCurrentMonth
                          ? "#0f172a"
                          : "#cbd5e1",
                    background: isSelected
                      ? THEME
                      : isToday
                        ? THEME_LIGHT
                        : "transparent",
                    fontWeight: isSelected || isToday ? "600" : "400",
                    border: isToday && !isSelected ? `1px solid ${THEME}` : "none",
                    cursor: isPast ? "not-allowed" : "pointer",
                    opacity: isPast ? 0.4 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected && !isPast) {
                      e.currentTarget.style.background = "#f1f5f9";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected && !isPast) {
                      e.currentTarget.style.background = isToday ? THEME_LIGHT : "transparent";
                    }
                  }}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          {/* Footer actions */}
          <div style={dp.footer}>
            <button type="button" onClick={handleClear} style={dp.footerBtn}>
              Clear
            </button>
            <button type="button" onClick={handleToday} style={{ ...dp.footerBtn, color: THEME, fontWeight: "600" }}>
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Styling classes for CustomDatePicker
const dp = {
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "12px",
  },
  navBtn: {
    background: "none",
    border: "1px solid #e2e8f0",
    borderRadius: "6px",
    width: "28px",
    height: "28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#64748b",
    outline: "none",
    transition: "background 0.1s",
  },
  navTitle: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#0f172a",
  },
  weekdaysGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    textAlign: "center",
    marginBottom: "6px",
  },
  weekday: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#94a3b8",
    padding: "4px 0",
  },
  daysGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "2px",
    marginBottom: "8px",
  },
  dayCell: {
    aspectRatio: "1",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    outline: "none",
    transition: "background 0.1s, color 0.1s",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    borderTop: "1px solid #f1f5f9",
    paddingTop: "10px",
    marginTop: "4px",
  },
  footerBtn: {
    background: "none",
    border: "none",
    fontSize: "12px",
    cursor: "pointer",
    color: "#64748b",
    padding: "4px 8px",
    borderRadius: "4px",
    outline: "none",
  },
};

const DEFAULT_FORM = {
  title: "",
  description: "",
  iterationPath: "",
  assignedTo: "",
  priority: 2,
  tags: "",
  targetDate: "",
};

export default function CreateAssignment({ onBack }) {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();

  /* ── org / project ── */
  const [orgProjects, setOrgProjects] = useState([]);   // [{ organization, projects }]
  const [orgLoading, setOrgLoading] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState("");
  const [selectedProject, setSelectedProject] = useState("");

  /* ── user stories ── */
  const [userStories, setUserStories] = useState([]);
  const [storiesLoading, setStoriesLoading] = useState(false);

  /* ── user story selection: "__new__" or a story id ── */
  const [selectedStory, setSelectedStory] = useState("__new__");

  /* ── child type (only visible when an existing story is picked) ── */
  const [childType, setChildType] = useState("Task");

  /* ── assignable users ── */
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [suggestingAssignee, setSuggestingAssignee] = useState(false);
  const [suggestedAssignee, setSuggestedAssignee] = useState("");
  const [workloadSummary, setWorkloadSummary] = useState({});

  /* ── iterations ── */
  const [iterations, setIterations] = useState([]);
  const [iterationsLoading, setIterationsLoading] = useState(false);

  /* ── form ── */
  const [form, setForm] = useState(DEFAULT_FORM);
  const [etaDaysText, setEtaDaysText] = useState("Loading ETA...");
  const [etaDetails, setEtaDetails] = useState(null);

  /* ── feedback ── */
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  /* ── TAG CHIPS STATE ── */
  const [tagChips, setTagChips] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const tagInputRef = useRef(null);
  const skipBlurRef = useRef(false);

  /* ── WORK ITEM SEARCH & AUTO-FILL STATE ── */
  const [workItemSearch, setWorkItemSearch] = useState("");
  const [workItemResults, setWorkItemResults] = useState([]);
  const [workItemSearching, setWorkItemSearching] = useState(false);
  const [workItemDropdownOpen, setWorkItemDropdownOpen] = useState(false);
  const [workItemSearchError, setWorkItemSearchError] = useState("");
  const [selectedWorkItem, setSelectedWorkItem] = useState(null);
  const workItemSearchWrapRef = useRef(null);

  /* auto-dismiss success banner after 5s */
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(""), 5000);
    return () => clearTimeout(t);
  }, [success]);

  /* close work item search dropdown on outside click */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (workItemSearchWrapRef.current && !workItemSearchWrapRef.current.contains(e.target)) {
        setWorkItemDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectWorkItem = (item) => {
    setSelectedWorkItem(item);
    setWorkItemDropdownOpen(false);
    setWorkItemSearchError("");
    setWorkItemSearch(item.wits_serial_number || "");

    // Auto-populate Title
    const newTitle = `${item.document_type || "DOCUMENT"} - Policy: ${item.policy_number || "N/A"} - ${item.primary_insured || "N/A"}`;

    // Auto-populate Description
    const receivedFormatted = item.received_datetime ? new Date(item.received_datetime).toLocaleString() : "N/A";
    const newDescription = `CFP Document Management Portal Assignment
--------------------------------------------------
• Work Item ID: ${item.wits_serial_number || "N/A"}
• Document Type: ${item.document_type || "N/A"}
• Policy Number: ${item.policy_number || "N/A"}
• Line of Business (LOB): ${item.lob || "N/A"}
• Agency Number: ${item.agency_no || "N/A"}
• Primary Insured: ${item.primary_insured || "N/A"}

Email Source Context:
• From: ${item.mail_from_address || "N/A"}
• Received: ${receivedFormatted}
• Subject: ${item.email_subject || "N/A"}
• Reviewer Comments: ${item.review_comments || "None"}`;

    const defaultAssignee = item.ado_assigned_to_email || item.assigned_to || item.verified_by || suggestedAssignee || (assignableUsers[0]?.mailAddress || assignableUsers[0]?.principalName) || "";
    const defaultAssigneeName = item.ado_assigned_to || item.assigned_to_name || defaultAssignee;

    setForm((prev) => ({
      ...prev,
      title: newTitle,
      description: newDescription,
      assignedTo: prev.assignedTo || defaultAssignee,
      assignedToDisplayName: prev.assignedToDisplayName || defaultAssigneeName,
    }));

    // Auto-populate Tags
    const tagsToAdd = [item.document_type, item.lob, item.wits_serial_number].filter(Boolean);
    setTagChips((prev) => {
      const combined = [...prev];
      tagsToAdd.forEach((t) => {
        if (!combined.includes(t)) combined.push(t);
      });
      return combined;
    });

    // Automatically trigger workflow suggest assignee for the initial intake department
    if (item.document_type) {
      loadSuggestedAssignee(item.document_type);
    } else {
      loadSuggestedAssignee();
    }
  };

  const handleExecuteSearch = async () => {
    const q = String(workItemSearch || "").trim();
    if (!q) {
      setWorkItemSearchError("Please enter a Work Item Number (e.g. REQ-034655 or 004210) to search.");
      setWorkItemResults([]);
      setWorkItemDropdownOpen(false);
      return;
    }

    try {
      setWorkItemSearching(true);
      setWorkItemSearchError("");
      const res = await callApi(
        instance,
        accounts,
        "GET",
        "/api/work-items/search",
        null,
        { params: { q, limit: 12 } }
      );
      const items = res?.data?.data || res?.data || [];
      const list = Array.isArray(items) ? items : [];
      setWorkItemResults(list);
      setWorkItemDropdownOpen(true);
      if (list.length === 0) {
        setWorkItemSearchError(`No work item found matching "${q}".`);
      }
    } catch (err) {
      console.error("Failed to search work item", err);
      setWorkItemSearchError(err?.message || "Failed to search work item.");
      setWorkItemResults([]);
    } finally {
      setWorkItemSearching(false);
    }
  };

  const handleClearWorkItem = () => {
    setSelectedWorkItem(null);
    setWorkItemSearch("");
    setWorkItemSearchError("");
    setWorkItemResults([]);
    setWorkItemDropdownOpen(false);

    // Reset input fields to clean state
    setForm((prev) => ({
      ...prev,
      title: "",
      description: "",
      assignedTo: "",
      assignedToDisplayName: "",
    }));
    setTagChips([]);
    setTagInput("");

    // Restore general assignee suggestion without document type filter
    loadSuggestedAssignee("");
  };

  /* =====================================================
     LOAD ORG/PROJECT LIST
  ===================================================== */
  useEffect(() => {
    const load = async () => {
      try {
        setOrgLoading(true);
        const res = await callApi(
          instance, accounts, "GET",
          "/api/assignments/organizations/projects"
        );
        const data = res?.data?.data || res?.data || [];
        const orgList = Array.isArray(data) ? data : [];
        setOrgProjects(orgList);
        if (orgList.length > 0) {
          const firstOrg = orgList[0];
          setSelectedOrg(firstOrg.organization);
          if (firstOrg.projects && firstOrg.projects.length > 0) {
            const adlsProj = firstOrg.projects.find(p => (p.name || p).toLowerCase() === "adls migration") || firstOrg.projects[0];
            const defaultProjName = adlsProj.name || adlsProj;
            setSelectedProject(defaultProjName);
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setOrgLoading(false);
      }
    };
    load();
  }, [instance, accounts]);

  /* =====================================================
     LOAD USER STORIES helper
  ===================================================== */
  const loadUserStories = useCallback(async (shouldResetSelected = false, overrideIteration = null) => {
    if (!selectedOrg || !selectedProject) {
      setUserStories([]);
      if (shouldResetSelected) setSelectedStory("__new__");
      return;
    }
    try {
      setStoriesLoading(true);
      if (shouldResetSelected) setSelectedStory("__new__");

      const currentIteration = overrideIteration !== null ? overrideIteration : form.iterationPath;
      const iterationParam = currentIteration ? `&iterationPath=${encodeURIComponent(currentIteration)}` : "";

      const res = await callApi(
        instance, accounts, "GET",
        `/api/assignments/user-stories?organization=${encodeURIComponent(selectedOrg)}&project=${encodeURIComponent(selectedProject)}${iterationParam}&_t=${Date.now()}`
      );
      const data = res?.data?.data || res?.data || [];
      setUserStories(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setUserStories([]);
    } finally {
      setStoriesLoading(false);
    }
  }, [selectedOrg, selectedProject, form.iterationPath, instance, accounts]);

  useEffect(() => {
    loadUserStories(true);
  }, [selectedOrg, selectedProject, form.iterationPath, loadUserStories]);

  /* =====================================================
     LOAD ITERATIONS when org + project are both set
  ===================================================== */
  useEffect(() => {
    if (!selectedOrg || !selectedProject) {
      setIterations([]);
      setForm((prev) => ({ ...prev, iterationPath: "" }));
      return;
    }
    const load = async () => {
      try {
        setIterationsLoading(true);
        const res = await callApi(
          instance, accounts, "GET",
          `/api/assignments/iterations?organization=${encodeURIComponent(selectedOrg)}&project=${encodeURIComponent(selectedProject)}&_t=${Date.now()}`
        );
        const data = res?.data?.data || res?.data || [];
        const fetched = Array.isArray(data) ? data : [];
        setIterations(fetched);
        if (fetched.length > 0) {
          const currentOrFirst = fetched.find((x) => x.attributes?.timeFrame === "current") || fetched[0];
          setForm((prev) => ({ ...prev, iterationPath: currentOrFirst.path || currentOrFirst.name }));
        } else {
          setForm((prev) => ({ ...prev, iterationPath: "" }));
        }
      } catch (err) {
        console.error(err);
        setIterations([]);
        setForm((prev) => ({ ...prev, iterationPath: "" }));
      } finally {
        setIterationsLoading(false);
      }
    };
    load();
  }, [selectedOrg, selectedProject, instance, accounts]);

  /* =====================================================
     LOAD ASSIGNABLE USERS when org or project changes
  ===================================================== */
  useEffect(() => {
    if (!selectedOrg) {
      setAssignableUsers([]);
      setForm((prev) => ({ ...prev, assignedTo: "" }));
      return;
    }
    const load = async () => {
      try {
        setUsersLoading(true);
        const projectParam = selectedProject ? `&project=${encodeURIComponent(selectedProject)}` : "";
        const res = await callApi(
          instance, accounts, "GET",
          `/api/assignments/assignable-users?organization=${encodeURIComponent(selectedOrg)}${projectParam}&_t=${Date.now()}`
        );
        const data = res?.data?.data || res?.data || [];
        setAssignableUsers(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        setAssignableUsers([]);
      } finally {
        setUsersLoading(false);
      }
    };
    load();
  }, [selectedOrg, selectedProject, instance, accounts]);

  /* =====================================================
     LOAD CALCULATED ETA DATE
  ===================================================== */
  const loadEtaDetails = useCallback(async () => {
    try {
      // Map priority to target duration: Priority 1 = 1 day, Priority 2 = 2 days, Priority 3 = 4 days, Priority 4 = 6 days
      let days = 2;
      const pri = Number(form.priority);
      if (pri === 1) days = 1;
      else if (pri === 2) days = 2;
      else if (pri === 3) days = 4;
      else if (pri === 4) days = 6;

      const res = await callApi(
        instance, accounts, "GET",
        `/api/assignments/calculate-eta?durationDays=${days}&_t=${Date.now()}`
      );
      const data = res?.data?.data || res?.data;
      if (data) {
        setEtaDetails(data);
        const dateFormatted = new Date(data.etaDate).toLocaleDateString("en-US", {
          year: "numeric", month: "long", day: "numeric", timeZone: "UTC"
        });

        let detailStr = `${dateFormatted} (${data.offsetDays} working day${data.offsetDays > 1 ? "s" : ""}`;
        if (data.holidaysSkipped?.length > 0 || data.weekendsSkipped > 0) {
          const skipParts = [];
          if (data.weekendsSkipped > 0) {
            skipParts.push(`${data.weekendsSkipped} weekend day${data.weekendsSkipped > 1 ? "s" : ""}`);
          }
          if (data.holidaysSkipped?.length > 0) {
            const hNames = data.holidaysSkipped.map(h => h.name).join(", ");
            skipParts.push(`holiday: ${hNames}`);
          }
          detailStr += ` - skipping ${skipParts.join(" & ")}`;
        }
        detailStr += `)`;
        setEtaDaysText(detailStr);
        setForm(prev => ({ ...prev, targetDate: data.etaDate }));
      }
    } catch (err) {
      console.error("Failed to load ETA details:", err);
    }
  }, [form.priority, instance, accounts]);

  useEffect(() => {
    loadEtaDetails();
  }, [loadEtaDetails]);

  /* =====================================================
     LOAD SUGGESTED ASSIGNEE helper
  ===================================================== */
  const loadSuggestedAssignee = useCallback(async (docTypeOverride = null) => {
    if (!selectedOrg || !selectedProject) {
      setSuggestedAssignee("");
      setWorkloadSummary({});
      return "";
    }
    try {
      setSuggestingAssignee(true);
      const iterationParam = form.iterationPath ? `&iterationPath=${encodeURIComponent(form.iterationPath)}` : "";
      const targetDocType = docTypeOverride !== null ? docTypeOverride : (selectedWorkItem?.document_type || "");
      const docTypeParam = targetDocType ? `&documentType=${encodeURIComponent(targetDocType)}` : "";

      const userEmailParam = accounts?.[0]?.username ? `&userEmail=${encodeURIComponent(accounts[0].username)}` : "";
      const userNameParam = accounts?.[0]?.name ? `&userName=${encodeURIComponent(accounts[0].name)}` : "";
      const res = await callApi(
        instance, accounts, "GET",
        `/api/assignments/suggest-assignee?organization=${encodeURIComponent(selectedOrg)}&project=${encodeURIComponent(selectedProject)}${iterationParam}${docTypeParam}${userEmailParam}${userNameParam}&_t=${Date.now()}`
      );
      const suggestion = res?.data?.data || res?.data;
      if (suggestion) {
        if (suggestion.workloadSummary) {
          setWorkloadSummary(suggestion.workloadSummary);
        }
        if (suggestion.suggestedUser) {
          const user = suggestion.suggestedUser;
          const email = user.mailAddress || user.principalName;
          const displayName = user.displayName || email;
          if (email) {
            setSuggestedAssignee(email);
            setForm((prev) => ({ ...prev, assignedTo: email, assignedToDisplayName: displayName }));
            return email;
          }
        }
      }
      setSuggestedAssignee("");
      return "";
    } catch (err) {
      console.error("Failed to load assignee suggestion:", err);
      setSuggestedAssignee("");
      return "";
    } finally {
      setSuggestingAssignee(false);
    }
  }, [selectedOrg, selectedProject, form.iterationPath, selectedWorkItem?.document_type, instance, accounts]);

  // Trigger suggestAssignee only once initial project & iterations are resolved, or when work item changes
  useEffect(() => {
    if (!selectedOrg || !selectedProject || iterationsLoading) return;
    loadSuggestedAssignee();
  }, [selectedOrg, selectedProject, selectedWorkItem, iterationsLoading, loadSuggestedAssignee]);

  /* =====================================================
     DERIVED
  ===================================================== */
  const isNew = selectedStory === "__new__";
  const projects = orgProjects.find((o) => o.organization === selectedOrg)?.projects || [];

  const isAssigneeLoading = usersLoading || suggestingAssignee;

  const isFormIncomplete =
    !selectedOrg ||
    !selectedProject ||
    !form.title ||
    !form.title.trim();

  const isSubmitDisabled = loading || isFormIncomplete;

  /* ─────── Options for SearchableSelect ─────── */
  const orgOptions = orgProjects.map((o) => ({ value: o.organization, label: o.organization }));
  const projectOptions = projects.map((p) => ({ value: p.name, label: p.name }));

  const storyOptions = [
    { value: "__new__", label: "＋ New User Story" },
    ...userStories.map((us) => ({
      value: String(us.id),
      label: us.title,
      sub: `#${us.id}`,
    })),
  ];

  const userOptions = (() => {
    if (!selectedOrg) return [{ value: "", label: "Select org first" }];
    if (usersLoading) return [{ value: "", label: "Loading users…" }];
    const baseOptions = assignableUsers
      .filter((u) => !u.displayName.toLowerCase().includes("build service"))
      .map((u) => {
        const email = (u.mailAddress || u.principalName || "").toLowerCase();
        const loadCount = workloadSummary[email] || 0;
        let loadText = "Idle";
        if (loadCount > 0) {
          loadText = `${loadCount} Active Task${loadCount > 1 ? "s" : ""}`;
        }
        return {
          value: u.mailAddress || u.principalName,
          label: u.displayName,
          sub: `${u.mailAddress || ""} · Workload: ${loadText}`
        };
      });

    // If an auto-assigned user is not in the base list, include them as an option
    if (form.assignedTo && !baseOptions.some(o => (o.value || "").toLowerCase() === form.assignedTo.toLowerCase())) {
      baseOptions.unshift({
        value: form.assignedTo,
        label: form.assignedToDisplayName || form.assignedTo,
        sub: selectedWorkItem ? "Assigned by Workflow Routing" : "Suggested from Hierarchy (Lowest Workload)"
      });
    }

    return baseOptions;
  })();

  const iterationOptions = iterations.map((it) => ({
    value: it.path || it.name,
    label: it.name,
  }));

  const priorityOptions = [
    { value: "1", label: "1 — Critical" },
    { value: "2", label: "2 — High" },
    { value: "3", label: "3 — Medium" },
    { value: "4", label: "4 — Low" },
  ];

  const childTypeOptions = [
    { value: "Task", label: "Task" },
    { value: "Bug", label: "Bug" },
  ];

  /* =====================================================
     HANDLERS
  ===================================================== */
  const handleOrgChange = (val) => {
    setSelectedOrg(val);
    setSelectedProject("");
    setForm((prev) => ({ ...prev, assignedTo: "" }));
    setError("");
    setSuccess("");
  };

  const handleProjectChange = (val) => {
    setSelectedProject(val);
    setForm((prev) => ({
      ...prev,
      iterationPath: "",
      assignedTo: "",
      title: "",
      description: "",
      tags: "",
    }));
    setTagChips([]);
    setTagInput("");
    setError("");
    setSuccess("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancel = () => {
    setForm({
      ...DEFAULT_FORM,
      assignedTo: suggestedAssignee,
      iterationPath:
        iterations.length > 0
          ? iterations[iterations.length - 1].path || iterations[iterations.length - 1].name
          : "",
    });
    setSelectedStory("__new__");
    setChildType("Task");
    setError("");
    setSuccess("");
    setTagChips([]);
    setTagInput("");
    if (typeof onBack === "function") onBack();
  };

  /* ── SUBMIT ── */
  const handleSubmit = async () => {
    if (!form.title.trim()) {
      setError("Title is required.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const payload = {
        organization: selectedOrg,
        project: selectedProject,
        title: form.title,
        description: form.description,
        iterationPath: form.iterationPath,
        assignedTo: form.assignedTo,
        priority: Number(form.priority),
        tags: tagChips.join("; "),
        targetDate: form.targetDate || undefined,
        parentId: !isNew ? selectedStory : undefined,
        witsSerialNumber: selectedWorkItem?.wits_serial_number || undefined,
      };

      let res;
      let label;

      if (isNew) {
        res = await callApi(instance, accounts, "POST", "/api/assignments/user-story", payload);
        label = "User Story";
      } else {
        res = await callApi(instance, accounts, "POST", "/api/assignments/task", {
          ...payload,
          type: childType,
        });
        label = childType;
      }

      const id = res?.data?.data?.id || res?.data?.id || res?.id || "";
      const generatedWitsNum = res?.data?.data?.workItemNum || res?.data?.workItemNum || "";
      const successText = `${label} created successfully${id ? ` · ADO #${id}` : ""}${generatedWitsNum ? ` · Work Item ID: ${generatedWitsNum}` : ""}.`;
      setSuccess(successText);

      // Reset work item selection
      handleClearWorkItem();

      // Refresh suggestion dynamically after creation to account for the updated workload
      const freshSuggestedEmail = await loadSuggestedAssignee();

      setForm({
        ...DEFAULT_FORM,
        assignedTo: freshSuggestedEmail,
        iterationPath:
          iterations.length > 0
            ? iterations[iterations.length - 1].path || iterations[iterations.length - 1].name
            : "",
      });
      setSelectedStory("__new__");
      setTagChips([]);
      setTagInput("");
      await loadUserStories();
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* =====================================================
     RENDER
  ===================================================== */
  return (
    <div style={s.page} id="ado-assignments-container">
      <style>{`
        #ado-assignments-container input:focus,
        #ado-assignments-container textarea:focus,
        #ado-assignments-container .ado-focusable:focus {
          border-color: ${THEME} !important;
          box-shadow: 0 0 0 3px ${THEME_LIGHT} !important;
          outline: none !important;
        }
        #ado-assignments-container button:focus,
        #ado-assignments-container input:focus,
        #ado-assignments-container textarea:focus {
          outline: none !important;
        }
        @keyframes spinAnimation {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes successFadeOut {
          0%   { opacity: 1; transform: translateY(0); }
          70%  { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-6px); }
        }
        .success-auto-dismiss {
          animation: successFadeOut 5s ease forwards;
        }
      `}</style>
      {/* HEADER */}
      <div style={s.header}>
        <div style={s.breadcrumb}>
          <span>Document Index Hub</span>
          {" "}
          &gt;{" "}
          <span style={{ color: "#0f172a", fontWeight: "600" }}>Manual Assignment</span>
        </div>
        <h1 style={s.title}>Manual Assignment</h1>
        <p style={s.sub}>
          Manually create work items, tasks, and user stories in Azure DevOps.
        </p>
      </div>

      <div style={{ ...s.formCard, borderLeft: `6px solid ${THEME}` }}>
        {/* SEARCH & AUTO-FILL BY WORK ITEM */}
        <div
          ref={workItemSearchWrapRef}
          style={{
            background: "#f8fbff",
            border: "1px solid #bfdbfe",
            borderRadius: "12px",
            padding: "16px",
            marginBottom: "24px",
            position: "relative",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "24px", height: "24px", borderRadius: "6px", background: "#eff6ff", color: "#2563eb" }}>
                <Sparkles size={14} />
              </div>
              <span style={{ fontSize: "12px", fontWeight: "700", color: "#1e3a8a", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Search & Auto-Fill by Work Item ID
              </span>
            </div>
            {selectedWorkItem && (
              <button
                type="button"
                onClick={handleClearWorkItem}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  border: "none",
                  background: "transparent",
                  color: "#ef4444",
                  fontSize: "11px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                <X size={13} />
                Clear Selection
              </button>
            )}
          </div>

          <p style={{ fontSize: "11px", color: "#64748b", margin: "0 0 12px 32px" }}>
            Type a Work Item Number (e.g. <code>REQ-034655</code> or <code>004210</code>) and click Search to browse matching records and select to auto-populate.
          </p>

          {/* Search Input and Button */}
          <div style={{ position: "relative" }}>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <div style={{ position: "relative", flex: 1 }}>
                <Search size={15} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }} />
                <input
                  type="text"
                  value={workItemSearch}
                  onChange={(e) => {
                    setWorkItemSearch(e.target.value);
                    if (workItemSearchError) setWorkItemSearchError("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleExecuteSearch();
                    }
                  }}
                  placeholder="Enter Work Item Number (e.g. REQ-034655 or 004210)..."
                  style={{
                    width: "100%",
                    height: "38px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    background: "#ffffff",
                    padding: "0 12px 0 36px",
                    fontSize: "13px",
                    color: "#0f172a",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <button
                type="button"
                onClick={handleExecuteSearch}
                disabled={workItemSearching || !workItemSearch.trim()}
                style={{
                  height: "38px",
                  padding: "0 18px",
                  borderRadius: "8px",
                  border: "none",
                  background: workItemSearching || !workItemSearch.trim() ? "#93c5fd" : "#2563eb",
                  color: "#ffffff",
                  fontSize: "13px",
                  fontWeight: "600",
                  cursor: workItemSearching || !workItemSearch.trim() ? "not-allowed" : "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  whiteSpace: "nowrap",
                  boxShadow: "0 1px 2px rgba(37,99,235,0.2)",
                  transition: "all 0.15s ease",
                }}
              >
                {workItemSearching ? (
                  <>
                    <RefreshCw size={14} style={{ animation: "spin 0.8s linear infinite" }} />
                    <span>Searching…</span>
                  </>
                ) : (
                  <>
                    <Search size={14} />
                    <span>Search</span>
                  </>
                )}
              </button>
            </div>

            {/* Suggestions Dropdown on Search */}
            {workItemDropdownOpen && (
              <div style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                marginTop: "6px",
                background: "#ffffff",
                border: "1px solid #cbd5e1",
                borderRadius: "10px",
                boxShadow: "0 10px 25px rgba(15, 23, 42, 0.15)",
                zIndex: 40,
                maxHeight: "300px",
                overflowY: "auto",
              }}>
                {workItemResults.length === 0 ? (
                  <div style={{ padding: "14px 16px", fontSize: "12px", color: "#64748b", textAlign: "center" }}>
                    {workItemSearching ? "Searching work items…" : "No matching work items found"}
                  </div>
                ) : (
                  <>
                    <div style={{ padding: "8px 12px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", fontSize: "11px", fontWeight: "600", color: "#64748b" }}>
                      Select a work item to auto-populate details ({workItemResults.length} found):
                    </div>
                    {workItemResults.map((item) => {
                      const isAlreadyCreated = Boolean(item.has_assignment === 1 || item.ado_user_story_id);
                      const targetAdoId = item.ado_user_story_id;

                      const handleItemClick = () => {
                        if (isAlreadyCreated) {
                          if (targetAdoId) {
                            navigate(`/request-pool?id=${targetAdoId}&from=manual-assignment`, { state: { from: "/manual-assignment" } });
                          } else {
                            navigate(`/request-pool?q=${encodeURIComponent(item.wits_serial_number)}&from=manual-assignment`, { state: { from: "/manual-assignment" } });
                          }
                        } else {
                          handleSelectWorkItem(item);
                        }
                      };

                      return (
                        <div
                          key={item.work_item_id || item.wits_serial_number}
                          onClick={handleItemClick}
                          style={{
                            padding: "10px 14px",
                            borderBottom: "1px solid #f1f5f9",
                            cursor: "pointer",
                            transition: "background 0.1s",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "10px",
                            background: isAlreadyCreated ? "#fafafa" : "#ffffff",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = isAlreadyCreated ? "#f1f5f9" : "#eff6ff")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = isAlreadyCreated ? "#fafafa" : "#ffffff")}
                        >
                          <div style={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                              <span style={{
                                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                fontSize: "12px",
                                fontWeight: "700",
                                color: "#1e293b",
                                background: "#f1f5f9",
                                padding: "2px 6px",
                                borderRadius: "4px",
                                border: "1px solid #e2e8f0",
                              }}>
                                {item.wits_serial_number}
                              </span>
                              <span style={{ fontSize: "12px", fontWeight: "600", color: "#0f172a" }}>
                                {item.document_type || "UNKNOWN"}
                              </span>
                              {item.policy_number && (
                                <span style={{ fontSize: "11px", color: "#6d28d9", background: "#f5f3ff", padding: "1px 6px", borderRadius: "999px", border: "1px solid #c4b5fd", fontWeight: "600" }}>
                                  {item.policy_number}
                                </span>
                              )}
                              {isAlreadyCreated && (
                                <span style={{
                                  fontSize: "11px",
                                  color: "#475569",
                                  background: "#e2e8f0",
                                  padding: "1px 7px",
                                  borderRadius: "999px",
                                  border: "1px solid #cbd5e1",
                                  fontWeight: "600",
                                }}>
                                  Already Created {targetAdoId ? `(#${targetAdoId})` : ""}
                                </span>
                              )}
                            </div>
                          </div>

                          {isAlreadyCreated ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (targetAdoId) {
                                  navigate(`/request-pool?id=${targetAdoId}&from=manual-assignment`, { state: { from: "/manual-assignment" } });
                                } else {
                                  navigate(`/request-pool?q=${encodeURIComponent(item.wits_serial_number)}&from=manual-assignment`, { state: { from: "/manual-assignment" } });
                                }
                              }}
                              style={{
                                padding: "5px 12px",
                                borderRadius: "6px",
                                border: "1px solid #cbd5e1",
                                background: "#f1f5f9",
                                color: "#334155",
                                fontSize: "11.5px",
                                fontWeight: "600",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                transition: "all 0.15s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "#e2e8f0";
                                e.currentTarget.style.color = "#0f172a";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "#f1f5f9";
                                e.currentTarget.style.color = "#334155";
                              }}
                            >
                              <ExternalLink size={12} />
                              View
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectWorkItem(item);
                              }}
                              style={{
                                padding: "4px 12px",
                                borderRadius: "6px",
                                border: "none",
                                background: "#2563eb",
                                color: "#ffffff",
                                fontSize: "11.5px",
                                fontWeight: "600",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                              }}
                            >
                              Select
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>

          {workItemSearchError && !workItemDropdownOpen && (
            <div style={{ marginTop: "8px", fontSize: "12px", color: "#dc2626", fontWeight: "500" }}>
              {workItemSearchError}
            </div>
          )}

          {/* Selected Work Item Banner */}
          {selectedWorkItem && (
            <div style={{
              marginTop: "12px",
              padding: "10px 14px",
              background: "#ffffff",
              border: "1px solid #86efac",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                <CheckCircle2 size={16} color="#16a34a" />
                <div style={{ fontSize: "12px", color: "#1e293b" }}>
                  <strong>Linked to {selectedWorkItem.wits_serial_number}</strong> ({selectedWorkItem.document_type || "DOCUMENT"} · Policy: {selectedWorkItem.policy_number || "N/A"}) — Details automatically populated below.
                </div>
              </div>
            </div>
          )}
        </div>

        {(() => {
          const isParentLocked = selectedStory !== "__new__";
          return (
            <div style={s.field}>
              <label style={s.label}>
                <Calendar size={13} style={{ color: THEME, flexShrink: 0 }} />
                <span>Iteration Path / Sprint</span>
                {isParentLocked && <span style={{ fontSize: "10px", color: THEME, marginLeft: "6px", fontWeight: "bold" }}>(Locked by Parent)</span>}
              </label>
              <SearchableSelect
                id="ado-iteration-select"
                options={iterationOptions}
                value={form.iterationPath}
                onChange={(val) => setForm((prev) => ({ ...prev, iterationPath: val }))}
                placeholder={
                  iterationsLoading
                    ? "Loading iterations…"
                    : "Select sprint"
                }
                disabled={iterationsLoading || isParentLocked}
                loading={iterationsLoading}
              />
            </div>
          );
        })()}

        <div style={{ height: 16 }} />

        {/* User Story selector (searchable) */}
        <div style={s.field}>
          <label style={s.label}>
            <Layers size={13} style={{ color: THEME, flexShrink: 0 }} />
            <span>Assign With</span>
          </label>
          <SearchableSelect
            id="ado-story-select"
            options={storyOptions}
            value={selectedStory}
            onChange={(val) => {
              setSelectedStory(val);
              setError("");
              setSuccess("");
            }}
            placeholder={
              !selectedProject
                ? "Select project first"
                : storiesLoading
                  ? "Loading stories…"
                  : "Select user story"
            }
            disabled={!selectedProject || storiesLoading}
            loading={storiesLoading}
            showSearch={true}
          />
        </div>

        {/* DYNAMIC — work item type (only when existing story selected) */}
        {(selectedStory !== "__new__") && (
          <div style={{ ...s.field, marginTop: 14 }}>
            <label style={s.label}>
              <Bookmark size={13} style={{ color: THEME, flexShrink: 0 }} />
              <span>Work Item Type</span>
            </label>
            <SearchableSelect
              id="ado-child-type-select"
              options={childTypeOptions}
              value={childType}
              onChange={(val) => setChildType(val)}
              placeholder="Select type"
            />
          </div>
        )}

        <div style={{ height: 16 }} />

        <div style={s.field}>
          <label style={s.label}>
            <FileEdit size={13} style={{ color: THEME, flexShrink: 0 }} />
            <span>Title</span>
            <span style={s.req}> *</span>
          </label>
          <input
            style={s.input}
            type="text"
            name="title"
            value={form.title}
            onChange={handleChange}
            placeholder="Enter work item title summary"
          />
        </div>

        <div style={{ height: 14 }} />

        {/* Description */}
        <div style={s.field}>
          <label style={s.label}>
            <AlignLeft size={13} style={{ color: THEME, flexShrink: 0 }} />
            <span>Description</span>
          </label>
          <textarea
            style={s.textarea}
            name="description"
            value={form.description}
            onChange={handleChange}
            placeholder="Describe details for the work item…"
          />
        </div>

        <div style={{ height: 16 }} />

        <div style={s.row2}>
          <div style={s.field}>
            <label style={s.label}>
              <User size={13} style={{ color: THEME, flexShrink: 0 }} />
              <span>Assigned To</span>
              <span style={s.req}> *</span>
              {isAssigneeLoading ? (
                <span style={{ fontSize: "11px", color: THEME, marginLeft: "6px", fontWeight: "600", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                  <RefreshCw size={11} style={{ animation: "spinAnimation 1s linear infinite" }} />
                  Refetching assignee…
                </span>
              ) : selectedWorkItem ? (
                <span style={{ fontSize: "11px", color: THEME, marginLeft: "6px", fontWeight: "600" }}>
                  (Auto-Assigned by Workflow)
                </span>
              ) : null}
            </label>
            <SearchableSelect
              id="ado-assigned-to-select"
              options={userOptions}
              value={form.assignedTo}
              onChange={(val) => setForm((prev) => ({ ...prev, assignedTo: val }))}
              placeholder={
                isAssigneeLoading
                  ? "Refetching assignee…"
                  : selectedWorkItem
                    ? "Auto-assigned by workflow…"
                    : "Select assignee"
              }
              disabled={isAssigneeLoading || !selectedOrg || Boolean(selectedWorkItem)}
              loading={isAssigneeLoading}
              showSearch={true}
            />
          </div>

          <div style={s.field}>
            <label style={s.label}>
              <Clock size={13} style={{ color: THEME, flexShrink: 0 }} />
              <span>Target Date / ETA</span>
            </label>
            <input
              type="text"
              style={{ ...s.input, backgroundColor: "#f8fafc", borderColor: "#e2e8f0", color: "#475569", cursor: "not-allowed", fontWeight: "600" }}
              value={etaDaysText}
              disabled
            />
          </div>
        </div>

        <div style={{ height: 14 }} />

        <div style={s.row2}>
          <div style={s.field}>
            <label style={s.label}>
              <Tag size={13} style={{ color: THEME, flexShrink: 0 }} />
              <span>Tags</span>
            </label>
            <div
              style={s.tagEditor}
              onClick={() => tagInputRef.current && tagInputRef.current.focus()}
            >
              {tagChips.map((chip, idx) => (
                <span key={idx} style={s.tagChip}>
                  {chip}
                  <button
                    type="button"
                    style={s.tagChipRemove}
                    onClick={(e) => {
                      e.stopPropagation();
                      setTagChips((prev) => prev.filter((_, i) => i !== idx));
                    }}
                    aria-label={`Remove tag ${chip}`}
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                ref={tagInputRef}
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ";") {
                    e.preventDefault();
                    const val = tagInput.replace(/;/g, "").trim();
                    if (val && !tagChips.includes(val)) {
                      setTagChips((prev) => [...prev, val]);
                    }
                    setTagInput("");
                    skipBlurRef.current = true;
                  } else if (e.key === "Backspace" && tagInput === "" && tagChips.length > 0) {
                    setTagChips((prev) => prev.slice(0, -1));
                  }
                }}
                onBlur={(e) => {
                  if (skipBlurRef.current) {
                    skipBlurRef.current = false;
                    return;
                  }
                  const val = e.target.value.replace(/;/g, "").trim();
                  if (val && !tagChips.includes(val)) {
                    setTagChips((prev) => [...prev, val]);
                    setTagInput("");
                  }
                }}
                placeholder={tagChips.length === 0 ? "Type tag, press Enter/;" : ""}
                style={s.tagInput}
              />
            </div>
            <span style={{ fontSize: "11px", color: "#94a3b8", marginTop: "5px" }}>
              Press Enter or ; to add · Click × to remove
            </span>
          </div>
        </div>

        {/* Feedback */}
        {error && <div style={s.errorBox}>{error}</div>}
        {success && (
          <div style={s.successBox} className="success-auto-dismiss">
            {success}
          </div>
        )}

        {/* Actions */}
        <div style={s.actionRow}>
          <button
            style={s.cancelBtn}
            className="ado-focusable"
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </button>

          <button
            style={{
              ...s.submitBtn,
              ...(isSubmitDisabled
                ? {
                  opacity: 0.55,
                  cursor: "not-allowed",
                  backgroundColor: "#94a3b8",
                  borderColor: "#94a3b8",
                  boxShadow: "none",
                }
                : {
                  backgroundColor: THEME,
                  color: "#ffffff",
                  cursor: "pointer",
                  opacity: 1,
                }),
            }}
            className="ado-focusable"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            title={
              isAssigneeLoading
                ? "Please wait while assignee is being fetched…"
                : isFormIncomplete
                  ? "Please fill in required fields (Title and Assigned To)."
                  : "Create work item"
            }
          >
            {loading
              ? "Creating…"
              : isAssigneeLoading
                ? "Fetching Assignee…"
                : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* =====================================================
   STYLES
===================================================== */
const s = {
  page: {
    minHeight: "100vh",
    boxSizing: "border-box",
    maxWidth: "100%",
    padding: "clamp(12px, 4vw, 24px)",
  },

  header: { marginBottom: "20px" },

  title: {
    margin: 0,
    fontSize: "26px",
    fontWeight: "700",
    color: "#0f172a",
  },

  sub: {
    color: "#64748b",
    marginTop: "5px",
    fontSize: "14px",
  },

  formCard: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    padding: "clamp(16px, 5vw, 28px)",
    boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
    boxSizing: "border-box",
  },

  field: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    width: "100%",
    boxSizing: "border-box",
  },

  label: {
    fontSize: "11px",
    fontWeight: "700",
    marginBottom: "6px",
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },

  req: { color: "#dc2626" },

  input: {
    padding: "11px 13px",
    border: "1px solid #cbd5e1",
    borderRadius: "9px",
    fontSize: "14px",
    background: "#ffffff",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    fontFamily: "inherit",
    color: "#0f172a",
    height: "44px",
  },

  select: {
    padding: "11px 13px",
    border: "1px solid #cbd5e1",
    borderRadius: "9px",
    fontSize: "14px",
    background: "#ffffff",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    height: "44px",
    fontFamily: "inherit",
    color: "#0f172a",
  },

  textarea: {
    padding: "11px 13px",
    border: "1px solid #cbd5e1",
    borderRadius: "9px",
    fontSize: "14px",
    background: "#ffffff",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    resize: "vertical",
    minHeight: "110px",
    fontFamily: "inherit",
    color: "#0f172a",
  },

  tagEditor: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    alignItems: "center",
    minHeight: "44px",
    padding: "6px 10px",
    border: "1px solid #cbd5e1",
    borderRadius: "9px",
    background: "#ffffff",
    cursor: "text",
    boxSizing: "border-box",
    width: "100%",
  },
  tagChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "3px 8px",
    borderRadius: "99px",
    background: "rgba(37,99,235,0.08)",
    color: "rgb(37,99,235)",
    fontSize: "12px",
    fontWeight: "600",
    whiteSpace: "nowrap",
  },
  tagChipRemove: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "rgb(37,99,235)",
    fontSize: "15px",
    lineHeight: 1,
    padding: "0",
    display: "flex",
    alignItems: "center",
    opacity: 0.7,
  },
  tagInput: {
    flex: 1,
    minWidth: "120px",
    border: "none",
    outline: "none",
    fontSize: "13px",
    color: "#0f172a",
    fontFamily: "inherit",
    background: "transparent",
    padding: "2px 4px",
  },

  row2: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "14px",
    boxSizing: "border-box",
  },

  row3: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: "14px",
    boxSizing: "border-box",
  },

  divider: {
    height: "1px",
    background: "#e2e8f0",
    margin: "16px 0",
  },

  errorBox: {
    marginTop: "14px",
    background: "#fee2e2",
    color: "#991b1b",
    padding: "12px 14px",
    borderRadius: "10px",
    fontSize: "13px",
  },

  successBox: {
    marginTop: "14px",
    background: "#dcfce7",
    color: "#166534",
    padding: "12px 14px",
    borderRadius: "10px",
    border: "1px solid #86efac",
    fontSize: "13px",
  },

  actionRow: {
    marginTop: "20px",
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    flexWrap: "wrap",
  },

  cancelBtn: {
    padding: "11px 22px",
    border: "1px solid #cbd5e1",
    borderRadius: "9px",
    background: "#ffffff",
    color: "#475569",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "14px",
  },

  submitBtn: {
    padding: "11px 26px",
    border: "none",
    borderRadius: "9px",
    background: THEME,
    color: "#ffffff",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "14px",
    fontFamily: "inherit",
    minWidth: "120px",
    transition: "background 0.15s",
  },

  breadcrumb: {
    fontSize: "12px",
    color: "#94a3b8",
    marginBottom: "6px",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "clamp(12px, 3vw, 18px)",
    width: "100%",
    boxSizing: "border-box",
    marginTop: "10px",
  },

  menuCard: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "clamp(14px, 3vw, 18px)",
    cursor: "pointer",
    transition: "all 0.2s ease",
    width: "100%",
    boxSizing: "border-box",
    textAlign: "left",
  },

  cardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
    marginBottom: "12px",
    flexWrap: "wrap",
  },

  iconBox: {
    background: "#f1f5f9",
    padding: "10px",
    borderRadius: "10px",
    flexShrink: 0,
    display: "inline-flex",
  },

  cardTitle: {
    margin: 0,
    fontSize: "16px",
    fontWeight: "600",
    color: "#0f172a",
  },

  cardDesc: {
    marginTop: "5px",
    fontSize: "13px",
    color: "#64748b",
    lineHeight: "1.4",
  },

  backLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
    color: THEME,
    cursor: "pointer",
    fontWeight: "600",
    background: "none",
    border: "none",
    padding: 0,
    marginBottom: "15px",
    outline: "none",
  },
};

/* =====================================================
   SEARCHABLE SELECT STYLES
===================================================== */
const ss = {
  trigger: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    padding: "10px 13px",
    border: "1px solid #cbd5e1",
    borderRadius: "9px",
    fontSize: "14px",
    background: "#ffffff",
    color: "#0f172a",
    fontFamily: "inherit",
    cursor: "pointer",
    textAlign: "left",
    transition: "border-color 0.15s, box-shadow 0.15s",
    height: "44px",
    boxSizing: "border-box",
    gap: "8px",
  },
  triggerText: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "14px",
  },
  triggerSub: {
    color: "#94a3b8",
    fontSize: "12px",
  },
  chevron: {
    width: 16,
    height: 16,
    flexShrink: 0,
    color: "#64748b",
    transition: "transform 0.2s ease",
  },
  panel: {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    zIndex: 9999,
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    boxShadow: "0 8px 32px rgba(15,23,42,0.12), 0 2px 8px rgba(15,23,42,0.06)",
    overflow: "hidden",
  },
  searchWrap: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 12px",
    borderBottom: "1px solid #f1f5f9",
    background: "#f8fafc",
  },
  searchIcon: {
    width: 16,
    height: 16,
    flexShrink: 0,
  },
  searchInput: {
    flex: 1,
    border: "none",
    background: "transparent",
    outline: "none",
    fontSize: "13px",
    color: "#0f172a",
    fontFamily: "inherit",
  },
  clearBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#94a3b8",
    fontSize: "18px",
    lineHeight: 1,
    padding: "0 2px",
    display: "flex",
    alignItems: "center",
  },
  optionList: {
    maxHeight: "220px",
    overflowY: "auto",
    padding: "6px",
  },
  option: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
    padding: "9px 10px",
    borderRadius: "7px",
    border: "none",
    cursor: "pointer",
    fontSize: "14px",
    fontFamily: "inherit",
    textAlign: "left",
    transition: "background 0.1s",
    boxSizing: "border-box",
  },
  optionLabel: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  optionSub: {
    fontSize: "11px",
    color: "#94a3b8",
    flexShrink: 0,
  },
  checkIcon: {
    width: 16,
    height: 16,
    flexShrink: 0,
  },
  emptyMsg: {
    padding: "16px 12px",
    textAlign: "center",
    color: "#94a3b8",
    fontSize: "13px",
  },
};
